import classNames from "classnames";
import {
  addMilliseconds,
  addMinutes,
  differenceInMilliseconds,
  isAfter,
  isToday,
  setHours,
  startOfHour
} from "date-fns";
import { Range } from "immutable";
import { upperFirst } from "lodash";
import { observer } from "mobx-react";
import React, { useRef } from "react";
import { intl } from "@fidget/common/i18n/TypedIntl";
import meetingPopupStyles from "../../components/calendar/meetingPopup.css";
import { ScheduleNew } from "../../models/calendar/MeetingPopup";
import styles from "./dropdownMeetingEdit.css";
import {
  DateTimeDropdown,
  DatetimeDropdownMode
} from "../../components/dateTimePicker/dateTimeDropdown";
import { DAY_MILLIS, MINUTE_MILLIS, TimeUtils } from "@fidget/common/utils/timeUtils";
import { useStore } from "../../models";

interface Props {
  popupState: ScheduleNew;
  className?: string;
}

interface FormElements {
  date?: HTMLLIElement;
  start?: HTMLLIElement;
  end?: HTMLLIElement;
}

export type FormField = keyof FormElements;

export const DropdownMeetingEditForm = observer(({ popupState, className }: Props) => {
  const { calendarStore } = useStore();
  const elements = useRef<FormElements>({});

  const renderTimeFrame = (meeting: ScheduleNew, meetingStart: Date) => {
    const dayStart = startOfHour(setHours(meetingStart, 0)).getTime();
    const dayStop = startOfHour(setHours(meetingStart, 24)).getTime();

    const { meetings: meetingsThatDay } = calendarStore.filterByDay(meeting.start);

    const timeOptions = TimeUtils.dailyCalendarRange(
      dayStart,
      dayStop,
      15 * MINUTE_MILLIS
    ).skipWhile((v) => v.getTime() <= Date.now());

    const unavailableSlots = Range(0)
      .map((i) => meetingsThatDay[i])
      .take(meetingsThatDay.length)
      .filterNot((m) => m!.start === meeting.start.getTime())
      .flatMap((m) =>
        Range(0, m!.duration / MINUTE_MILLIS, 15).map((duration) => addMinutes(m!.start, duration))
      )
      .toOrderedSet();

    const availableSlots = timeOptions.toOrderedSet().subtract(unavailableSlots);

    const durationOptions = timeOptions
      .concat(new Date(dayStop))
      .filter((value) => isAfter(value, meeting.start))
      .take(4)
      .toArray();

    const lastPossible = durationOptions[durationOptions.length - 1];

    if (lastPossible) {
      const outOfRange = isAfter(addMilliseconds(meetingStart, meeting.duration), lastPossible);
      if (outOfRange) {
        durationOptions.pop();
        calendarStore.changeMeetingDuration(differenceInMilliseconds(lastPossible, meetingStart));
      }
    }

    return (
      <div className={styles["input-group"]}>
        <DateTimeDropdown
          startingPoint={meetingStart}
          mode={DatetimeDropdownMode.TIME}
          options={availableSlots.toArray()}
          inputRef={(el) => (elements.current.start = el)}
          onChange={(value) => calendarStore.changeMeetingStartTime(value || new Date(""))}
          id={"meeting_start_edit_dropdown"}
        />
        <DateTimeDropdown
          startingPoint={addMilliseconds(meetingStart, meeting.duration)}
          mode={DatetimeDropdownMode.TIME_WITH_DURATION}
          options={durationOptions}
          inputRef={(el) => (elements.current.end = el)}
          onChange={(value) =>
            calendarStore.changeMeetingDuration(differenceInMilliseconds(value, meetingStart))
          }
          id={"meeting_end_edit_dropdown"}
        />
      </div>
    );
  };

  const render = () => {
    const meetingStart = isNaN(popupState.start.getTime())
      ? popupState.lastValidStart
      : popupState.start;

    const now = TimeUtils.copyHoursAndMinutes(meetingStart, new Date());
    now.setSeconds(0);
    now.setMilliseconds(0);

    const startingDate = now.valueOf();
    const dateOptions = Range(0)
      .map((i) => new Date(startingDate + i * DAY_MILLIS))
      .take(30);

    return (
      <div className={classNames(className)}>
        <div className={meetingPopupStyles["meeting-relative-date"]}>
          {meetingStart
            ? isToday(meetingStart)
              ? intl.formatMessage("date.today")
              : upperFirst(intl.formatRelativeDays(meetingStart))
            : intl.formatMessage("calendar.meeting.invalidDate")}
        </div>
        <div className={styles["input-group"]}>
          <DateTimeDropdown
            startingPoint={popupState.start}
            mode={DatetimeDropdownMode.DAY}
            options={dateOptions.filter((date) => isAfter(date, Date.now())).toArray()}
            inputRef={(el) => (elements.current.end = el)}
            onChange={(value) => calendarStore.changeMeetingStartTime(value || new Date(""))}
            id={"date_edit_dropdown"}
          />
        </div>
        {renderTimeFrame(popupState, meetingStart)}
      </div>
    );
  };

  return render();
});
